/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';
import {
  getTopicPath,
  getTopicAuthor,
  isTopicVisible,
  isTopicDiscussed,
  getTopicCustomIcon,
  getTopicCustomLabel,
  getGroupTopicParticipants,
  isUserTopicAuthor,
  isGroupTopic,
} from '@e180/domain/lib/topic/util';

import styles from './TopicCard.style';

import TopicCardTitle from './TopicCardTitle';
import LazyTopicDetailsMain from 'src/shared/components/domain/topic/details/LazyTopicDetailsMain';
import GroupBraindateBanner from 'src/shared/components/domain/braindate/banner/GroupBraindateBanner';
import ConnectedTopicFavoriteButton from 'src/shared/components/domain/topic/buttons/ConnectedTopicFavoriteButton';
import UserBanner from 'src/shared/components/domain/user/banner/UserBanner';
import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';
import Card from 'src/shared/ui/component/data-display/Card';

import type { Conversation } from '@e180/domain/lib/conversation/type';
import type { Topic } from '@e180/domain/lib/topic/type';
import type { User } from '@e180/domain/lib/user/type';

type Props = {
  user: User,
  conversation?: Conversation,
  topic: Topic,
  isTopicDisabled: ?boolean,
  isRejected?: boolean,
  animate?: boolean,
  trackFMP?: boolean,
  handleClick?: () => void,
  handleAnimatedCardUnmount?: () => void,
  // Self passed
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const TopicCardDumb = ({
  user,
  conversation,
  topic,
  isTopicDisabled,
  isRejected,
  animate,
  handleAnimatedCardUnmount,
  trackFMP,
  handleClick,
  intl,
}: Props): React.Node => {
  const author = getTopicAuthor(topic);
  const icon = getTopicCustomIcon(topic) || {};
  const label = getTopicCustomLabel(topic, intl.locale);

  const isOwn = isUserTopicAuthor(user, topic);
  const isGroupBraindate = isGroupTopic(topic);
  const hasConversation = isTopicDiscussed(topic);
  const isVisible = isTopicVisible(topic);

  let path;

  if (isOwn) {
    if (isGroupBraindate) {
      // If the topic is rejected, we want to go on the edit page directly, not the conversation
      path = isRejected
        ? `${getTopicPath(topic)}edit`
        : `${getTopicPath(topic)}conversation`;
    } else {
      path = getTopicPath(topic);
    }
  } else {
    if (hasConversation) {
      path = `${getTopicPath(topic)}conversation`;
    } else {
      path = getTopicPath(topic);
    }
  }

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [accentColor] = useEventAccentColor();

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(
    () => () => {
      if (animate && handleAnimatedCardUnmount) {
        handleAnimatedCardUnmount();
      }
    },
    [],
  );

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onMouseOver = () => LazyTopicDetailsMain.preload();

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ accentColor });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const labelElt = <p className={classes.label}>{label}</p>;

  let bannerElt;
  let favoriteBtn;
  let overlayElt;
  let iconElt;

  if (isGroupBraindate) {
    if (typeof conversation === 'object') {
      bannerElt = <GroupBraindateBanner conversation={conversation} />;
    } else {
      // $FlowIssue
      const participants = getGroupTopicParticipants(topic);

      bannerElt = (
        <GroupBraindateBanner topic={topic} participants={participants} />
      );
    }
  } else {
    bannerElt = (
      <UserBanner
        user={author}
        withIcon={
          icon && typeof icon === 'object' && Object.keys(icon).length > 0
        }
      />
    );
  }

  if (!isOwn) {
    favoriteBtn = (
      <ConnectedTopicFavoriteButton topic={topic} source="Topic card" />
    );
  }

  if (animate) {
    overlayElt = <span className={classes.overlay} />;
  }

  if (icon && typeof icon === 'object' && icon.original) {
    iconElt = <img className={classes.icon} src={icon.original} alt={label} />;
  }

  return (
    <Card
      isDisabled={!!isTopicDisabled}
      isFaded={isOwn && !isVisible && !isGroupBraindate}
    >
      <Link
        className={classes.root}
        to={path}
        onClick={handleClick}
        onMouseOver={onMouseOver}
      >
        {overlayElt}
        <span className={classes.body}>
          <TopicCardTitle topic={topic} trackFMP={trackFMP} />
          {bannerElt}
        </span>
        {favoriteBtn}
        {iconElt || labelElt}
      </Link>
    </Card>
  );
};

export default compose(React.memo, injectIntl)(TopicCardDumb);
